import { useState, useEffect, memo } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

export const AuthenticatedImage = memo(function AuthenticatedImage({ 
  attachmentId, 
  alt, 
  className 
}: { 
  attachmentId: string; 
  alt: string; 
  className?: string;
}) {
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    
    async function fetchImage() {
      try {
        // Security: encode the id so the fetch can never escape the
        // /api/chat/attachments/ path (e.g. via '../' or absolute URLs).
        const response = await fetch(`/api/chat/attachments/${encodeURIComponent(attachmentId)}`, {
          credentials: 'include',
          redirect: 'follow'
        });
        
        if (!response.ok) {
          throw new Error('Failed to load image');
        }
        
        const blob = await response.blob();
        if (!cancelled) {
          const url = URL.createObjectURL(blob);
          setImageSrc(url);
          setIsLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(true);
          setIsLoading(false);
        }
      }
    }
    
    fetchImage();
    
    return () => {
      cancelled = true;
      if (imageSrc) {
        URL.revokeObjectURL(imageSrc);
      }
    };
  }, [attachmentId]);

  if (isLoading) {
    return (
      <div className={cn("flex items-center justify-center bg-muted rounded-lg", className)} style={{ minHeight: 48 }}>
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !imageSrc) {
    return (
      <div className={cn("flex items-center justify-center bg-muted rounded-lg text-[10px] text-muted-foreground", className)} style={{ minHeight: 48 }}>
        Failed to load
      </div>
    );
  }

  return (
    <img 
      src={imageSrc}
      alt={alt}
      className={className}
      loading="lazy"
    />
  );
});

export default AuthenticatedImage;
